import { Checkbox, Dropzone, Flex, NumberField, TextField } from '@approvalmax/ui/src/components';
import { Table } from '@approvalmax/ui/src/components';
import { intl, numberHelpers } from '@approvalmax/utils';
import { constants } from 'modules/common';
import {
    isFieldReadOnly,
    isFieldVisible,
} from 'pages/requestForm/containers/netsuite/NetSuiteRequestForm/NetSuiteRequestForm.helpers';

import { CategoryCell } from '../CategoryCell/CategoryCell';
import { CustomerSelector } from '../CustomerSelector/CustomerSelector';
import { ExpenseDateCell } from '../ExpenseDateCell/ExpenseDateCell';
import { GeneralSelector } from '../GeneralSelector/GeneralSelector';
import { TaxCodeCell } from '../TaxCodeCell/TaxCodeCell';
import { fieldMinHeight, linesNamePrefix } from './ExpenseSection.constants';
import { useColumnsDefinitionData } from './ExpenseSection.hooks';
import { messages } from './ExpenseSection.messages';
import { ColumnDefinitionForExpenseReportExtended } from './ExpenseSection.types';

export const getColumnsDefinition = (params: ReturnType<typeof useColumnsDefinitionData>) => {
    const {
        fieldsSettings,
        accounts,
        currencies,
        departments,
        classes,
        locations,
        currencyPrecision,
        attachmentUploadingLineIds,
        onPreviewAttachment,
    } = params;

    const coulumDefinition: ColumnDefinitionForExpenseReportExtended[] = [];

    if (isFieldVisible(fieldsSettings.refLineNumber)) {
        coulumDefinition.push({
            id: 'refNo',
            name: messages.columnRefNo,
            minWidth: 80,
            verticalAlign: 'top',
            cellComponent: (props) => (
                <Table.FormCell name={`${linesNamePrefix}.${props.item.index}.refLineNumber`} {...props}>
                    {(params) => (
                        <NumberField
                            {...params}
                            placeholder={messages.columnRefNoPlaceholder}
                            clearable={false}
                            bordered={false}
                            size='small'
                        />
                    )}
                </Table.FormCell>
            ),
        });
    }

    if (isFieldVisible(fieldsSettings.date)) {
        coulumDefinition.push({
            id: 'expenseDate',
            name: messages.columnDate,
            minWidth: 160,
            verticalAlign: 'top',
            cellComponent: (props) => (
                <ExpenseDateCell cellProps={props} disabled={isFieldReadOnly(fieldsSettings.date)} />
            ),
        });
    }

    if (isFieldVisible(fieldsSettings.category)) {
        coulumDefinition.push({
            id: 'category',
            name: messages.columnCategory,
            minWidth: 274,
            verticalAlign: 'top',
            cellComponent: (props) => <CategoryCell cellProps={props} />,
        });
    }

    if (isFieldVisible(fieldsSettings.account)) {
        coulumDefinition.push({
            id: 'expenseAccount',
            name: messages.columnExpenseAccount,
            minWidth: 218,
            verticalAlign: 'top',
            cellComponent: (props) => (
                <GeneralSelector
                    name='expenseAccount'
                    items={accounts}
                    placeholder={messages.columnExpenseAccountPlaceholder}
                    cellProps={props}
                />
            ),
        });
    }

    if (isFieldVisible(fieldsSettings.foreignAmount)) {
        coulumDefinition.push({
            id: 'foreignAmount',
            name: messages.columnForeignAmount,
            minWidth: 160,
            verticalAlign: 'top',
            cellComponent: (props) => (
                <Table.FormCell name={`${linesNamePrefix}.${props.item.index}.foreignAmount`} {...props}>
                    {(params) => (
                        <NumberField
                            {...params}
                            allowFloat
                            precision={currencies.find(({ id }) => id === props.item.currency?.id)?.precision ?? 2}
                            placeholder={messages.columnForeignAmountPlaceholder}
                            clearable={false}
                            min={0}
                            max={constants.netSuiteConstants.maxCustomNumericFieldAmount}
                            bordered={false}
                            size='small'
                            disabled={isFieldReadOnly(fieldsSettings.foreignAmount)}
                        />
                    )}
                </Table.FormCell>
            ),
        });
    }

    if (isFieldVisible(fieldsSettings.currency)) {
        coulumDefinition.push({
            id: 'currency',
            name: messages.columnCurrency,
            minWidth: 120,
            verticalAlign: 'top',
            cellComponent: (props) => (
                <GeneralSelector
                    name='currency'
                    items={currencies}
                    disabled={isFieldReadOnly(fieldsSettings.currency)}
                    cellProps={props}
                />
            ),
        });
    }

    if (isFieldVisible(fieldsSettings.exchangeRate)) {
        coulumDefinition.push({
            id: 'exchangeRate',
            name: messages.columnExchangeRate,
            minWidth: 100,
            verticalAlign: 'top',
            cellComponent: (props) => (
                <Table.FormCell name={`${linesNamePrefix}.${props.item.index}.exchangeRate`} {...props}>
                    {(params) => (
                        <NumberField
                            {...params}
                            allowFloat
                            precision={8}
                            max={constants.netSuiteConstants.exchangeRateMaxValue}
                            min={0}
                            placeholder={messages.columnExchangeRatePlaceholder}
                            clearable={false}
                            bordered={false}
                            size='small'
                            disabled={isFieldReadOnly(fieldsSettings.exchangeRate)}
                        />
                    )}
                </Table.FormCell>
            ),
        });
    }

    if (isFieldVisible(fieldsSettings.amount)) {
        coulumDefinition.push({
            id: 'amount',
            name: messages.columnAmount,
            minWidth: 160,
            verticalAlign: 'top',
            cellComponent: (props) => (
                <Table.FormCell name={`${linesNamePrefix}.${props.item.index}.amount`} {...props}>
                    {(params) => (
                        <NumberField
                            {...params}
                            allowFloat
                            precision={currencyPrecision}
                            max={constants.netSuiteConstants.maxTotalAmount}
                            min={0}
                            placeholder={messages.columnAmountPlaceholder}
                            clearable={false}
                            bordered={false}
                            size='small'
                            disabled={isFieldReadOnly(fieldsSettings.amount)}
                        />
                    )}
                </Table.FormCell>
            ),
        });
    }

    if (isFieldVisible(fieldsSettings.taxCode)) {
        coulumDefinition.push({
            id: 'taxCode',
            name: messages.columnTaxCode,
            minWidth: 160,
            verticalAlign: 'top',
            cellComponent: (props) => (
                <TaxCodeCell cellProps={props} disabled={isFieldReadOnly(fieldsSettings.taxCode)} />
            ),
        });
    }

    if (isFieldVisible(fieldsSettings.taxRate)) {
        coulumDefinition.push({
            id: 'taxRate',
            name: messages.columnTaxRate,
            minWidth: 65,
            verticalAlign: 'top',
            cellComponent: (props) => (
                <Table.Cell {...props} disabled>
                    <Flex alignItems='center' justifyContent='end' minHeight={fieldMinHeight}>
                        {numberHelpers.isNumber(props.item.taxRate) ? `${props.item.taxRate}%` : ''}
                    </Flex>
                </Table.Cell>
            ),
        });
    }

    if (isFieldVisible(fieldsSettings.taxAmount)) {
        coulumDefinition.push({
            id: 'taxAmount',
            name: messages.columnTaxAmount,
            minWidth: 85,
            verticalAlign: 'top',
            cellComponent: (props) => (
                <Table.Cell {...props} disabled>
                    <Flex alignItems='center' justifyContent='end' minHeight={fieldMinHeight}>
                        {numberHelpers.isNumber(props.item.amount) && props.item.taxRate
                            ? intl.formatNumber(props.item.taxAmount ?? 0, currencyPrecision)
                            : ''}
                    </Flex>
                </Table.Cell>
            ),
        });
    }

    if (isFieldVisible(fieldsSettings.grossAmount)) {
        coulumDefinition.push({
            id: 'grossAmount',
            name: messages.columnGrossAmount,
            minWidth: 100,
            verticalAlign: 'top',
            cellComponent: (props) => (
                <Table.FormCell name={`${linesNamePrefix}.${props.item.index}.grossAmount`} {...props}>
                    {(params) => (
                        <NumberField
                            {...params}
                            allowFloat
                            precision={currencyPrecision}
                            max={constants.netSuiteConstants.maxTotalAmount}
                            min={0}
                            placeholder={messages.columnGrossAmountPlaceholder}
                            clearable={false}
                            bordered={false}
                            disabled={isFieldReadOnly(fieldsSettings.grossAmount)}
                            size='small'
                        />
                    )}
                </Table.FormCell>
            ),
        });
    }

    if (isFieldVisible(fieldsSettings.memo)) {
        coulumDefinition.push({
            id: 'memo',
            name: messages.columnMemo,
            minWidth: 274,
            verticalAlign: 'top',
            cellComponent: (props) => (
                <Table.FormCell name={`${linesNamePrefix}.${props.item.index}.memo`} {...props}>
                    {(params) => (
                        <TextField
                            {...params}
                            placeholder={messages.columnMemoPlaceholder}
                            maxLength={constants.netSuiteConstants.memoMaxLength}
                            multiline
                            autoHeight
                            changeOnBlur={false}
                            disabled={isFieldReadOnly(fieldsSettings.memo)}
                            bordered={false}
                            rows={1}
                            size='small'
                        />
                    )}
                </Table.FormCell>
            ),
        });
    }

    if (isFieldVisible(fieldsSettings.department)) {
        coulumDefinition.push({
            id: 'department',
            name: messages.columnDepartment,
            minWidth: 274,
            verticalAlign: 'top',
            cellComponent: (props) => (
                <GeneralSelector
                    name='department'
                    items={departments}
                    disabled={isFieldReadOnly(fieldsSettings.department)}
                    placeholder={messages.columnDepartmentPlaceholder}
                    cellProps={props}
                />
            ),
        });
    }

    if (isFieldVisible(fieldsSettings.class)) {
        coulumDefinition.push({
            id: 'class',
            name: messages.columnClass,
            minWidth: 180,
            verticalAlign: 'top',
            cellComponent: (props) => (
                <GeneralSelector
                    name='class'
                    items={classes}
                    disabled={isFieldReadOnly(fieldsSettings.class)}
                    placeholder={messages.columnClassPlaceholder}
                    cellProps={props}
                />
            ),
        });
    }

    if (isFieldVisible(fieldsSettings.location)) {
        coulumDefinition.push({
            id: 'location',
            name: messages.columnLocation,
            minWidth: 180,
            verticalAlign: 'top',
            cellComponent: (props) => (
                <GeneralSelector
                    name='location'
                    items={locations}
                    disabled={isFieldReadOnly(fieldsSettings.location)}
                    placeholder={messages.columnLocationPlaceholder}
                    cellProps={props}
                />
            ),
        });
    }

    if (isFieldVisible(fieldsSettings.customer)) {
        coulumDefinition.push({
            id: 'customer',
            name: messages.columnCustomer,
            minWidth: 218,
            verticalAlign: 'top',
            cellComponent: (props) => (
                <CustomerSelector cellProps={props} disabled={isFieldReadOnly(fieldsSettings.customer)} />
            ),
        });
    }

    if (isFieldVisible(fieldsSettings.billable)) {
        coulumDefinition.push({
            id: 'isBillable',
            name: messages.columnBillable,
            minWidth: 55,
            verticalAlign: 'top',
            cellComponent: (props) => (
                <Table.FormCell name={`${linesNamePrefix}.${props.item.index}.isBillable`} {...props}>
                    {(params) => (
                        <Flex alignItems='center' minHeight={fieldMinHeight}>
                            <Checkbox
                                {...params}
                                size='small'
                                checked={props.item.customer ? Boolean(props.item.isBillable) : false}
                                disabled={!props.item.customer || isFieldReadOnly(fieldsSettings.billable)}
                            />
                        </Flex>
                    )}
                </Table.FormCell>
            ),
        });
    }

    if (isFieldVisible(fieldsSettings.attachment)) {
        coulumDefinition.push({
            id: 'attachment',
            name: messages.columnLineAttachment,
            minWidth: 250,
            verticalAlign: 'top',
            cellComponent: (props) => (
                <Table.FormCell name={`${linesNamePrefix}.${props.item.index}.attachment`} {...props}>
                    {(params) => (
                        <Flex alignItems='center' minHeight={fieldMinHeight}>
                            <Dropzone
                                {...params}
                                hideZone
                                maxSize={constants.uploadsConstants.COMMENT_MAX_ATTACHMENT_FILESIZE}
                                progress={attachmentUploadingLineIds.includes(props.item.id)}
                                maxFiles={1}
                                onShow={(file) => file.id && onPreviewAttachment(file.id)}
                                disabled={isFieldReadOnly(fieldsSettings.attachment)}
                                size='small'
                            />
                        </Flex>
                    )}
                </Table.FormCell>
            ),
        });
    }

    if (isFieldVisible(fieldsSettings.receipt)) {
        coulumDefinition.push({
            id: 'isReceipt',
            name: messages.columnReceipt,
            minWidth: 55,
            verticalAlign: 'top',
            cellComponent: (props) => (
                <Table.FormCell name={`${linesNamePrefix}.${props.item.index}.isReceipt`} {...props}>
                    {(params) => (
                        <Flex alignItems='center' minHeight={fieldMinHeight}>
                            <Checkbox {...params} size='small' disabled={isFieldReadOnly(fieldsSettings.receipt)} />
                        </Flex>
                    )}
                </Table.FormCell>
            ),
        });
    }

    if (isFieldVisible(fieldsSettings.nonReimbursable)) {
        coulumDefinition.push({
            id: 'isNonReimbursable',
            name: messages.columnNonReimbursable,
            minWidth: 100,
            verticalAlign: 'top',
            cellComponent: (props) => (
                <Table.FormCell name={`${linesNamePrefix}.${props.item.index}.isNonReimbursable`} {...props}>
                    {(params) => (
                        <Flex alignItems='center' minHeight={fieldMinHeight}>
                            <Checkbox
                                {...params}
                                size='small'
                                disabled={
                                    props.item.isCorporateCreditCard || isFieldReadOnly(fieldsSettings.nonReimbursable)
                                }
                            />
                        </Flex>
                    )}
                </Table.FormCell>
            ),
        });
    }

    if (isFieldVisible(fieldsSettings.corporateCreditCard)) {
        coulumDefinition.push({
            id: 'isCorporateCreditCard',
            name: messages.columnCorporateCard,
            minWidth: 100,
            verticalAlign: 'top',
            cellComponent: (props) => (
                <Table.FormCell name={`${linesNamePrefix}.${props.item.index}.isCorporateCreditCard`} {...props}>
                    {(params) => (
                        <Flex alignItems='center' minHeight={fieldMinHeight}>
                            <Checkbox
                                {...params}
                                size='small'
                                disabled={
                                    props.item.isNonReimbursable ||
                                    !props.item.expenseAccount ||
                                    isFieldReadOnly(fieldsSettings.corporateCreditCard)
                                }
                            />
                        </Flex>
                    )}
                </Table.FormCell>
            ),
        });
    }

    return coulumDefinition;
};
